import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { PaletteIcon, XIcon } from 'lucide-react'
import { Button } from '@repo/ui'

import { ROUTES } from 'routes'
import { useCurrentUserCompany } from 'hooks/api/useCompanyQuery'
import { useGetCompanyStyles } from 'hooks/api/useGetCompanyStyles'
import { Preview } from 'pages/main/StudioPage/components/Preview'
import { vhMinus } from 'utils/textZoom'

/**
 * See a certificate as the public sees it, WITHOUT activating it.
 *
 * ## Why a drawer and not a page
 *
 * This replaced a `/certificates/:id/preview` route. Studio already renders the
 * certificate preview in a right-hand pane, so a second preview on its own route, at a
 * different size, was a second surface for one job — the drift pattern this codebase keeps
 * losing to (§36). Same `Preview` component, two containers: Studio's split pane and this
 * drawer.
 *
 * Removing the page also removed a real bug rather than needing it fixed: that route's
 * breadcrumb sat 4px higher and its title 30px lower than the certificate page's, because
 * the header offset was inherited from whatever wrapped it. A drawer has no page header,
 * so there is nothing to misalign.
 *
 * ## Why activation is irrelevant here
 *
 * Activation writes a metering row and debits the ledger — it is billable. This drawer works
 * on any certificate, activated or not, without costing anything, and that is the whole point
 * of the surface.
 *
 * ⚠️ The REASON given here used to be "the public read path applies no activation filter,
 * because a shelf certificate must stay scannable for an in-store tap". That was true when it
 * was written and has been false since VRG-319, which made `created` and `inactive` refuse to
 * verify publicly at all (`verify.public.controller.ts`, the `certificate_not_activated`
 * branch — it redirects to `/certificate-not-active`). The conclusion survived the change by
 * luck rather than design: this drawer does not use the public verify path. It loads the
 * customer app's PREVIEW route over the Studio bridge (`?isPreview=true`, certificate id
 * pushed by postMessage), which is a different entry point and is not gated. Verified against
 * a live `inactive` certificate, which renders here in full while refusing to verify publicly.
 *
 * That difference is exactly why the header's "View live" hides on those two statuses while
 * this button does not — see `isViewLiveAvailable`.
 *
 * ## Width
 *
 * NOT sized to Studio's proportion, which is generous because that page is a two-pane split
 * by design. The certificate detail page behind this is dense, so the drawer takes only what
 * the phone actually needs — see `DRAWER_WIDTH`, which settles on 500 for a different reason
 * again (parity with the Filters drawer).
 *
 * What the phone needs is now 325px wide: the drawer opens at 125% of a `baseZoom` of 1,
 * which renders the mobile frame at 325x703. That clears the 450px of usable width inside
 * the pane's padding, so width is not what binds. HEIGHT is: see the measured thresholds at
 * `initialZoom` below. Short of them the preview scrolls rather than clipping (the pane's
 * `safe center` — see `Preview`).
 */

/**
 * 500, matching the Filters drawer exactly rather than being sized to the phone frame.
 *
 * The first version picked its own width from the content (520) and sat flush against the
 * viewport edge. That made it a second kind of drawer in an app that already has one —
 * and the house drawer is INSET: `margin: 4` with `borderRadius: 3`, so it floats over the
 * page rather than welding itself to the edge. Matching Filters means a user who has
 * opened one has already learned this one (§28).
 */
const DRAWER_WIDTH = 500

interface CertificatePreviewDrawerProps {
  open: boolean
  onClose: () => void
  certificateId: string
  /** Shown in the drawer header so it is obvious which record is being previewed. */
  title?: string
}

export const CertificatePreviewDrawer = ({
  open,
  onClose,
  certificateId,
  title,
}: CertificatePreviewDrawerProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: company } = useCurrentUserCompany()
  const { data: companyStylesResponse } = useGetCompanyStyles({ companyId: company?.id })

  // The SAVED theme, not Studio's dirty form state — this previews the certificate as it
  // stands today, not an in-progress restyle. Studio remains where you try changes.
  const savedTheme = useMemo(() => companyStylesResponse?.styles ?? {}, [companyStylesResponse])

  const goToStudio = () => {
    // Studio is a CROSS-SURFACE jump (not an ancestor of a certificate), so passing the
    // referrer is what makes the shared Header render "< Back to …" over there — §42. It
    // is also the answer to "Style in Studio needs a back button": the back affordance is
    // the referrer link, not a bespoke button.
    navigate(ROUTES.STUDIO, {
      state: { backTo: `${location.pathname}${location.search}`, backLabel: title || 'Certificate' },
    })
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          // The house drawer floats: inset by 16px with a 12px radius, rather than welded
          // to the viewport edge. Copied from Filters so the two read as one component.
          margin: 4,
          borderRadius: 3,
          // 32 is NOT breathing room to be tuned — it is `margin: 4` counted twice. MUI gives
          // a right-anchored Drawer `height: 100%`, so with a 16px margin on each side the
          // paper would overhang the viewport by exactly 32px and the bottom corner radius
          // would fall off screen. This cancels that, which means the drawer ALREADY spans
          // 16px to `vh - 16` and is full height. Any empty band inside it is the content's
          // doing, not this cap's — measured, the paper's bottom and the preview pane's
          // bottom now coincide.
          maxHeight: vhMinus(32),
          minWidth: DRAWER_WIDTH,
          maxWidth: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          // Kill MUI's dark-mode elevation overlay so header, body and footer read as one
          // uniform surface instead of three subtly different shades (same fix as Filters).
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      {/*
        Actions sit at the TOP, not in a footer.
        A footer is where a task is COMMITTED — Filters puts Clear/Apply there because
        those finish the job the drawer exists for. This drawer commits nothing; it is a
        viewing surface, and "Style in Studio" is navigation OUT of it. One lone button in
        a footer also reads as an unfinished form.
        The original crowding came from the DESCRIPTION sharing the row, not from the
        button — it was wrapping under it at drawer width. So the description moves to its
        own line beneath (§40) and the top row carries title + action + close, which fits
        comfortably in 500px.
      */}
      <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
          <Typography variant="body1" sx={{ flexShrink: 0 }}>Preview</Typography>

          <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
            <Button
              variant="secondary"
              startIcon={<PaletteIcon strokeWidth={1} />}
              onClick={goToStudio}
            >
              Style in Studio
            </Button>
            {/* X, not a trash glyph — this dismisses an overlay, it destroys nothing (§38). */}
            <IconButton
              onClick={onClose}
              aria-label="Close preview"
              sx={{ color: theme.palette.text.primary }}
            >
              <XIcon strokeWidth={1} />
            </IconButton>
          </Stack>
        </Stack>

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          How the certificate appears to anyone who opens it.
        </Typography>
      </Stack>

      {/*
        A real flex COLUMN, not a bare `flex: 1` box. The child needs a parent whose height
        is already resolved in order to take `height: 100%` from it — as `display: block`
        this was handing the pane no height at all to inherit, which is why the pane used to
        size itself off `100vh` instead and the two disagreed by ~108px.
      */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Preview
          formValues={savedTheme}
          previewPage="certificate"
          device="mobile"
          certificateId={certificateId}
          // 1, not Studio's 1.35. Studio previews inside a wide two-pane split; this drawer
          // is 500px and the phone came out bigger than the space holding it.
          baseZoom={1}
          // Opens at 125% — 325x703 with `baseZoom: 1`. At 100% the frame is 260px wide, a
          // thumbnail, when the entire reason to open this drawer is to READ what the
          // certificate says. 150% was tried first: it gives the phone's native 390x844
          // viewport 1:1, which is the prettier number and the worse one, since 844 needs
          // ~140px more viewport than 703 before the whole device fits.
          //
          // ⚠️ MEASURED, because the estimate was wrong. 125% shows the whole device down to
          // a ~915px viewport (940 fits, 900 is 14px over, 870 is 44px over) and stops
          // scrolling entirely at 1000px. An earlier note here claimed ~860px; that was
          // arithmetic rather than a measurement and it was optimistic. So at a typical
          // laptop's ~800px NEITHER zoom shows the whole phone — 125% is better, not
          // sufficient. It degrades properly rather than clipping: the pane's `safe center`
          // keeps the top reachable (verified at 640px), so the preview scrolls.
          initialZoom={1.25}
          // The pane fills THIS box rather than measuring itself against the viewport.
          //
          // It used to take `heightOffset={249}` / `stickyTop={60}` / `topGap={0}` — three
          // numbers restating, in viewport terms, a box whose size the parent already knew.
          // They did not agree with it: measured at both 800px and 1259px tall, the pane sat
          // 60px below this container's top and ended 48px above its bottom, so ~108px of the
          // drawer was permanently empty and no viewport height fixed it. Those props stay on
          // the component for Studio, whose pane genuinely does live in a scrolling page and
          // has to pin; here the container is the bound, so it says so.
          fillContainer
        />
      </Box>
    </Drawer>
  )
}
